import { useState, useCallback, useEffect, useRef } from "react";
import useAgentStore from "./useAgentStore";
import usePublicChatStore from "./usePublicChatStore";
import { parsePublicErrorMessage } from "./agentSessionsPanelUtils";

/**
 * Returns true when URL-driven session sync should clear local selection.
 * Skips clearing during start-chat transitions where local state leads the URL.
 */
export function shouldClearLocalSessionForUrlSync({
  requestedSessionId,
  selectedSessionId,
  startingChat,
  sessionDetailChatSessionId,
}) {
  const nextRequestedSessionId = requestedSessionId || null;
  if (nextRequestedSessionId === selectedSessionId) return false;
  if (selectedSessionId === "__pending__") return false;
  if (startingChat) return false;
  if (nextRequestedSessionId) return false;
  if (
    selectedSessionId &&
    sessionDetailChatSessionId &&
    selectedSessionId === sessionDetailChatSessionId
  ) {
    return false;
  }
  return true;
}

/**
 * Encapsulates session selection, new-chat, and start-chat logic
 * for both private and public modes.
 */
export default function useSessionActions({
  agentId,
  agentName,
  isPublicMode,
  composerDisabled,
  publicShareToken,
  publicIdentityPayload,
  publicUserDisplayName,
  initialSessionId,
  requestedSessionId,
  onSessionSelect,
  showSidebar,
  chatContext,
  // from usePublicSessions
  pub,
}) {
  const isSessionRequestControlled = requestedSessionId !== undefined;
  const initialRequestedSessionId = isSessionRequestControlled
    ? requestedSessionId
    : initialSessionId;
  const [selectedSessionId, setSelectedSessionId] = useState(
    initialRequestedSessionId || null
  );
  const [isNewChat, setIsNewChat] = useState(false);
  const [startingChat, setStartingChat] = useState(false);

  const initialLoadDone = useRef(false);
  const prevStatusRef = useRef(null);

  const pubRef = useRef(pub);
  pubRef.current = pub;

  // Selected session's status as reflected in the sessions list. This is the
  // surface-independent signal that a session reactivated out-of-band (e.g. an
  // artifact submitted on a separate page flips a COMPLETED session back to a
  // streamable status). Returns a primitive so it only re-renders on change.
  const selectedSessionListStatus = useAgentStore((s) => {
    if (!selectedSessionId || selectedSessionId === "__pending__") return null;
    const row = s.sessions.find(
      (session) => session?.chatSessionId === selectedSessionId
    );
    return row?.status || null;
  });

  const resolvePrivateClientId = useCallback(() => {
    if (isPublicMode) return null;
    const currentDetail = useAgentStore.getState().sessionDetail;
    if (
      currentDetail &&
      currentDetail.chatSessionId === selectedSessionId &&
      Number.isFinite(Number(currentDetail.clientId)) &&
      Number(currentDetail.clientId) > 0
    ) {
      return Number(currentDetail.clientId);
    }
    const selectedRow = useAgentStore
      .getState()
      .sessions.find(
        (session) => session?.chatSessionId === selectedSessionId
      );
    const rowClientId = Number(selectedRow?.clientId);
    if (Number.isFinite(rowClientId) && rowClientId > 0) {
      return rowClientId;
    }
    return null;
  }, [isPublicMode, selectedSessionId]);

  const clearSelectedSession = useCallback(
    (notify = true) => {
      setIsNewChat(false);
      setSelectedSessionId(null);
      if (!isPublicMode) {
        useAgentStore.getState().disconnectStream();
        useAgentStore.getState().resetLifecycle(null);
        useAgentStore.setState({
          sessionDetail: null,
          sendError: null,
          _workerStatuses: {},
        });
      } else {
        pubRef.current.resetPublicState();
      }
      if (notify && onSessionSelect) onSessionSelect(null);
    },
    [isPublicMode, onSessionSelect]
  );

  const handleNewChat = useCallback(() => {
    setIsNewChat(true);
    setSelectedSessionId(null);
    if (!isPublicMode) {
      useAgentStore.getState().disconnectStream();
      useAgentStore.getState().resetLifecycle(null);
      useAgentStore.setState({ sessionDetail: null, sendError: null, _workerStatuses: {} });
    } else {
      pubRef.current.resetPublicState();
    }
    if (onSessionSelect) onSessionSelect(null);
  }, [isPublicMode, onSessionSelect]);

  const handleSelectSession = useCallback(
    (chatSessionId) => {
      if (!isNewChat && chatSessionId === selectedSessionId) {
        clearSelectedSession();
        return;
      }
      setIsNewChat(false);
      setSelectedSessionId(chatSessionId);
      if (!isPublicMode) {
        // Disconnect old SSE, reset state. The SSE subscription effect
        // (keyed on selectedSessionId) will subscribe to the new session
        // and the SSE snapshot will provide the authoritative initial state.
        useAgentStore.getState().disconnectStream();
        // Establish the new session in the lifecycle so its SSE/REST events pass
        // the §4.1 session gate; the SSE snapshot provides the authoritative state.
        useAgentStore.getState().resetLifecycle(chatSessionId);
        useAgentStore.setState({
          sessionDetail: null,
          sessionDetailLoading: true,
          sendError: null,
          _workerStatuses: {},
        });
        if (onSessionSelect) onSessionSelect(chatSessionId);
        return;
      }
      const p = pubRef.current;
      const selectedRow = p.publicSessions.find(
        (session) => session?.chatSessionId === chatSessionId
      );
      const sessionToken =
        p.publicSessionTokenByChatSessionId[chatSessionId] ||
        selectedRow?.publicSessionToken ||
        "";
      if (!sessionToken) {
        p.setPublicError("Unable to open this session right now.");
        return;
      }
      p.setSelectedPublicSessionToken(sessionToken);
      p.notifyPublicSessionTokenChange(sessionToken);
      if (onSessionSelect) onSessionSelect(chatSessionId);
      p.loadPublicSessionDetail(sessionToken, { silent: true });
    },
    [clearSelectedSession, isNewChat, selectedSessionId, isPublicMode, onSessionSelect]
  );

  const handleStartChat = useCallback(
    async (message, imageUrls) => {
      if (isPublicMode) {
        if (!publicShareToken || composerDisabled) return;
        const p = pubRef.current;
        setStartingChat(true);
        setIsNewChat(false);
        setSelectedSessionId("__pending__");
        p.setPublicPendingUserMessage(message);
        p.setPublicError("");
        try {
          const userTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
          const publicStartPayload = {
            ...(publicIdentityPayload || {}),
            message,
            userTimezone,
            agentId,
          };
          if (imageUrls && imageUrls.length > 0) {
            publicStartPayload.imageUrls = imageUrls;
          }
          const response = await usePublicChatStore
            .getState()
            .startPublicChat(publicShareToken, publicStartPayload);
          const data = response?.data || {};
          const nextPublicSessionToken = data?.publicSessionToken || "";
          if (data?.chatSessionId && nextPublicSessionToken) {
            p.upsertPublicSessionTokenMapping(
              data.chatSessionId,
              nextPublicSessionToken
            );
            p.setSelectedPublicSessionToken(nextPublicSessionToken);
            p.notifyPublicSessionTokenChange(nextPublicSessionToken);
            setSelectedSessionId(data.chatSessionId);
            if (onSessionSelect) onSessionSelect(data.chatSessionId);
            const optimisticUserMsg = {
              messageId: `_optimistic_start_${Date.now()}`,
              role: "user",
              content: message,
              timestamp: new Date().toISOString(),
              imageUrls: imageUrls || undefined,
              senderDisplayName: publicUserDisplayName || undefined,
              _optimistic: true,
            };
            const initialDetail = {
              chatSessionId: data.chatSessionId,
              status: "IN_PROGRESS",
              source: "PUBLIC_CHAT",
              title: agentName || "Agent Chat",
              messages: [optimisticUserMsg],
            };
            // Public start opener: open a fresh turn for the new session id
            // (the reducer adopts it immediately) and lock the composer.
            useAgentStore.getState()._applyLifecycle({
              kind: "optimistic",
              sessionId: data.chatSessionId,
              action: "startChat",
            });
            useAgentStore.setState({
              sessionDetail: initialDetail,
              sendError: null,
            });
            p.setPublicError("");
            if (showSidebar) {
              await p.loadPublicSessions({ silent: true });
            }
            await p.loadPublicSessionDetail(nextPublicSessionToken, {
              silent: true,
            });
          } else {
            setIsNewChat(true);
            setSelectedSessionId(null);
            p.setPublicPendingUserMessage(null);
          }
        } catch (error) {
          // Start failed: abandon the turn and park the lifecycle idle so the
          // composer unlocks for a retry.
          useAgentStore.getState().resetLifecycle(null);
          pubRef.current.setPublicError(
            parsePublicErrorMessage(
              error,
              "Unable to start this chat session."
            )
          );
          pubRef.current.setPublicPendingUserMessage(null);
          setIsNewChat(true);
          setSelectedSessionId(null);
        } finally {
          setStartingChat(false);
        }
        return;
      }

      // Private mode
      setStartingChat(true);
      setIsNewChat(false);
      setSelectedSessionId("__pending__");
      const result = await useAgentStore
        .getState()
        .startChat(agentId, message, imageUrls, chatContext);
      setStartingChat(false);
      if (result?.chatSessionId) {
        setSelectedSessionId(result.chatSessionId);
        if (onSessionSelect) onSessionSelect(result.chatSessionId);
      } else {
        setIsNewChat(true);
        setSelectedSessionId(null);
      }
    },
    [
      isPublicMode,
      publicShareToken,
      composerDisabled,
      publicIdentityPayload,
      publicUserDisplayName,
      agentId,
      agentName,
      onSessionSelect,
      showSidebar,
      chatContext,
    ]
  );

  const handleSendPublicMessage = useCallback(
    async (message, imageUrls) => {
      if (!isPublicMode || composerDisabled) return;
      const nextMessage = String(message || "").trim();
      if (!nextMessage) return;
      const p = pubRef.current;
      if (!p.selectedPublicSessionToken) {
        await handleStartChat(nextMessage, imageUrls);
        return;
      }
      const result = await useAgentStore.getState().sendPublicMessage(
        selectedSessionId,
        p.selectedPublicSessionToken,
        agentId,
        nextMessage,
        imageUrls,
        publicUserDisplayName || undefined
      );
      if (result) {
        p.setPublicError("");
        if (showSidebar) {
          p.loadPublicSessions({ silent: true });
        }
      }
    },
    [
      isPublicMode,
      composerDisabled,
      handleStartChat,
      selectedSessionId,
      agentId,
      publicUserDisplayName,
      showSidebar,
    ]
  );

  // --- Effects ---

  // New-chat request nonce
  const previousNewChatRequestNonceRef = useRef(0);
  const handleNewChatRequestNonce = useCallback(
    (nonce) => {
      if (nonce === previousNewChatRequestNonceRef.current) return;
      previousNewChatRequestNonceRef.current = nonce;
      handleNewChat();
    },
    [handleNewChat]
  );

  // Select-session request
  const previousSelectSessionNonceRef = useRef(0);
  const handleSelectSessionRequest = useCallback(
    (request) => {
      if (!request) return;
      if (request.nonce === previousSelectSessionNonceRef.current) return;
      previousSelectSessionNonceRef.current = request.nonce;
      if (!request.sessionId) {
        clearSelectedSession();
        return;
      }
      handleSelectSession(request.sessionId);
    },
    [clearSelectedSession, handleSelectSession]
  );

  useEffect(() => {
    if (!isSessionRequestControlled) return;

    const nextRequestedSessionId = requestedSessionId || null;
    if (nextRequestedSessionId === selectedSessionId) return;

    const sessionDetailChatSessionId =
      useAgentStore.getState().sessionDetail?.chatSessionId || null;

    if (
      shouldClearLocalSessionForUrlSync({
        requestedSessionId: nextRequestedSessionId,
        selectedSessionId,
        startingChat,
        sessionDetailChatSessionId,
      })
    ) {
      clearSelectedSession(false);
      return;
    }

    if (!nextRequestedSessionId) return;

    setIsNewChat(false);
    setSelectedSessionId(nextRequestedSessionId);
    if (!isPublicMode) {
      useAgentStore.getState().disconnectStream();
      useAgentStore.getState().resetLifecycle(nextRequestedSessionId);
      useAgentStore.setState({
        sessionDetail: null,
        sessionDetailLoading: true,
        sendError: null,
        _workerStatuses: {},
      });
    }
  }, [
    clearSelectedSession,
    isPublicMode,
    isSessionRequestControlled,
    requestedSessionId,
    selectedSessionId,
    startingChat,
  ]);

  // Reset session state when agentId changes (e.g. navigating between workflows)
  const prevAgentIdRef = useRef(agentId);
  useEffect(() => {
    if (prevAgentIdRef.current && agentId && prevAgentIdRef.current !== agentId) {
      setSelectedSessionId(null);
      setIsNewChat(false);
      useAgentStore.getState().disconnectStream();
      useAgentStore.getState().resetLifecycle(null);
      useAgentStore.setState({ sessionDetail: null, sendError: null, _workerStatuses: {} });
    }
    prevAgentIdRef.current = agentId;
  }, [agentId]);

  // Initial session load — SSE subscription effect handles data loading.
  // Just mark loading so the UI shows a spinner until the SSE snapshot arrives.
  useEffect(() => {
    if (isPublicMode) return;
    if (initialRequestedSessionId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      useAgentStore.setState({ sessionDetailLoading: true });
    }
  }, [initialRequestedSessionId, isPublicMode]);

  // Disconnect on unmount
  useEffect(() => {
    return () => useAgentStore.getState().disconnectStream();
  }, []);

  // SSE subscription
  useEffect(() => {
    if (isPublicMode) {
      if (
        !pub.selectedPublicSessionToken ||
        !selectedSessionId ||
        selectedSessionId === "__pending__"
      ) {
        return;
      }
      useAgentStore.getState().subscribeToSession(selectedSessionId, {
        publicSessionToken: pub.selectedPublicSessionToken,
        reconnectWhileSubscribed: true,
      });
      return () => useAgentStore.getState().disconnectStream();
    }
    if (!selectedSessionId || selectedSessionId === "__pending__") return;
    useAgentStore.getState().subscribeToSession(selectedSessionId, {
      reconnectWhileSubscribed: true,
    });
    return () => useAgentStore.getState().disconnectStream();
  }, [isPublicMode, selectedSessionId, pub.selectedPublicSessionToken]);

  // Reopen the chat stream when an open session is reactivated out-of-band. The
  // effect above is keyed only on selectedSessionId, so a status flip on the
  // same session (artifact submitted elsewhere → COMPLETED back to streamable)
  // never re-subscribes. Declared after that effect so the initial subscription
  // wins the mount race; the store action skips when a live stream is already
  // subscribed.
  useEffect(() => {
    if (isPublicMode) return;
    useAgentStore
      .getState()
      .reopenReactivatedSessionStream(selectedSessionId, selectedSessionListStatus);
  }, [isPublicMode, selectedSessionId, selectedSessionListStatus]);

  // Agent-level SSE subscription
  useEffect(() => {
    if (isPublicMode) return;
    if (!agentId) return;
    useAgentStore.getState().subscribeToAgent(agentId);
    return () => useAgentStore.getState().disconnectAgentStream();
  }, [agentId, isPublicMode]);

  // Private session polling
  useEffect(() => {
    if (isPublicMode) return;
    if (!agentId) return;
    const id = setInterval(() => {
      useAgentStore.getState().fetchSessions(agentId, { silent: true });
    }, 10000);
    return () => clearInterval(id);
  }, [agentId, isPublicMode]);

  // Status-change effect — refresh session list when status changes
  const handleStatusChange = useCallback(
    (currentStatus) => {
      if (isPublicMode) return;
      if (
        prevStatusRef.current !== currentStatus &&
        selectedSessionId &&
        selectedSessionId !== "__pending__"
      ) {
        useAgentStore.getState().refreshSessionInList(selectedSessionId);
        if (
          prevStatusRef.current === "IN_PROGRESS" &&
          currentStatus &&
          currentStatus !== "IN_PROGRESS"
        ) {
          useAgentStore.getState().fetchSessions(agentId, { silent: true });
        }
      }
      prevStatusRef.current = currentStatus;
    },
    [isPublicMode, selectedSessionId, agentId]
  );

  return {
    selectedSessionId,
    setSelectedSessionId,
    isNewChat,
    startingChat,
    resolvePrivateClientId,
    handleNewChat,
    handleSelectSession,
    handleStartChat,
    handleSendPublicMessage,
    handleNewChatRequestNonce,
    handleSelectSessionRequest,
    handleStatusChange,
  };
}
